import React from 'react';
import PropTypes from 'prop-types';
import { useCubeQuery } from '@cubejs-client/react';
import { Col, Row, Spin, Statistic, Typography } from 'antd';
import chart from '../utils/chart'
import subquery from '../utils/subquery'

import AmLineChart from './amCharts/AmLineChart'
import AmPieChart from './amCharts/AmPieChart'
import AmBarChart from './amCharts/AmBarChart'
import AmMapChart from './amCharts/AmMapChart'
import ListTable from './ListTable'
import processCubeResults from "../processing/cubeProcessing"
import {chartColors} from "../theme/chartColors";

const { Title } = Typography

let amchartIdGlobal;

// to be removed...
let client // = 'fii'
let clientColors

/**
 * @const TypeToChartComponent
 * maps the chart type to the Component to be rendered
 */
const TypeToChartComponent = {
    noChart: ({ renderedCharts }) => {
        renderedCharts.current[amchartIdGlobal] = true
        // console.log('NO DATA RENDERED', renderedCharts)
        return <Title level={5} style={{ textAlign: 'center' }}>There were no distinctive data for the selected period of
            coverage</Title>
    },
    line: ({ resultSet, renderedCharts }) => {
        const chartType = 'line'
        const { data, seriesNames } = processCubeResults({ queryProps: { resultSet }, chartType, clientName: client })
        return (
            < AmLineChart
                data={data}
                seriesNames={seriesNames}
                amchartId={amchartIdGlobal}
                isDateAxis={true}
                styleProps={clientColors.line}
                renderedCharts={renderedCharts}
            />
        );

    },
    bar: ({ resultSet, orientation, hasLegend, isSentiment, isDateAxis, amchartTitle, renderedCharts }) => {
        const chartType = 'bar'

        const processArgs = {
            queryProps: { resultSet },
            chartType,
            isSentiment,
            clientName: client
        }

        const { data, seriesNames } = processCubeResults(processArgs)
        return (
            <AmBarChart
                data={data}
                seriesNames={seriesNames}
                amchartId={amchartIdGlobal}
                orientation={orientation}
                hasLegend={hasLegend}
                isSentiment={isSentiment}
                isDateAxis={isDateAxis}
                styleProps={{...clientColors.sentiments, ...clientColors.top10}}
                amchartTitle={amchartTitle}
                renderedCharts={renderedCharts}
            />
        );
    },
    pie: ({ resultSet, amchartTitle, isSentiment, renderedCharts }) => {
        const chartType = 'pie'

        const styleProps = {
            colorArray: amchartTitle.includes('Countries') ? clientColors.pie.colorArray : []
        }

        const processArgs = {
            queryProps: { resultSet },
            chartType,
            isSentiment,
            clientName: client
        }
        const { data, seriesNames } = processCubeResults(processArgs)
        return (
            <AmPieChart
                data={data}
                seriesNames={seriesNames}
                amchartId={amchartIdGlobal}
                styleProps={styleProps}
                amchartTitle={amchartTitle}
                isSentiment={isSentiment}
                renderedCharts={renderedCharts}
            />
        );
    },
    number: ({ resultSet }) => {
        return (
            <Row
                type="flex"
                justify="center"
                align="middle"
                style={{
                    height: '100%',
                }}
            >
                <Col>
                    {resultSet.seriesNames().map((s) => (
                        <Statistic value={resultSet.totalRow()[s.key]} />
                    ))}
                </Col>
            </Row>
        );
    },
    table: ({ resultSet, pivotConfig, renderedCharts }) => {
        const chartType = 'table'
        const processArgs = {
            queryProps: { resultSet, pivotConfig },
            chartType,
            clientName: client
        }
        const { data, columns } = processCubeResults(processArgs)
        return (
            <ListTable
                data={data}
                columns={columns}
                resultSet={resultSet}
                pivotConfig={pivotConfig}
                styleProps={clientColors.table}
                renderedCharts={renderedCharts}
                amchartId={amchartIdGlobal}
            />
        );
    },
    map: ({ resultSet, renderedCharts }) => {
        const chartType = 'map'
        const processArgs = {
            queryProps: { resultSet },
            chartType,
            clientName: client
        }

        const { data } = processCubeResults(processArgs)
        return <AmMapChart
            data={data}
            styleProps={clientColors.map}
            amchartId={amchartIdGlobal}
            renderedCharts={renderedCharts}
        />
    }
};


/**
 * @function TypeToMemoChartComponent
 * memoize the TypeToChartComponent
 */
const TypeToMemoChartComponent = Object.keys(TypeToChartComponent)
    .map((key) => ({
        [key]: React.memo(TypeToChartComponent[key]),
    }))
    .reduce((a, b) => ({ ...a, ...b }));

/**
 * @function renderChart
 * function to render the chart component
 * if resultSet is available render the component, else
 * check if there is an error, if not, render Spin
 */
const renderChart =
    (Component) =>
        ({ resultSet, error, pivotConfig, orientation, hasLegend, isSentiment, isDateAxis, amchartTitle, renderedCharts }) =>
            (resultSet && (
                <Component resultSet={resultSet} pivotConfig={pivotConfig} orientation={orientation}
                           hasLegend={hasLegend} isSentiment={isSentiment} isDateAxis={isDateAxis}
                           amchartTitle={amchartTitle} renderedCharts={renderedCharts} />
            )) ||
            (error && error.toString()) || <Spin/>;

const renderChartWSubQuery =
    (Component) =>
        ({ resultSet, error, pivotConfig, orientation, hasLegend, subqueryProps, isSentiment, isDateAxis, amchartTitle, renderedCharts }) =>
            (resultSet && subqueryProps.resultSet && (
                <Component resultSet={resultSet} pivotConfig={pivotConfig} orientation={orientation}
                    hasLegend={hasLegend} subqueryProps={subqueryProps} isSentiment={isSentiment}
                    isDateAxis={isDateAxis} amchartTitle={amchartTitle} renderedCharts={renderedCharts} />
            )) ||
            (error && error.toString()) || <Spin />;

/**
 * map the query type to the chart it should be rendered as and then call
 * the renderChart function
 * @function ChartRenderer
 * @param {Object} { vizState, amchartTitle, amchartId }
 */
function ChartRenderer({
    vizState,
    amchartId,
    cubejsApi,
    amchartTitle,
    clientName,
    renderedCharts
}) {
    const { query, chartType, pivotConfig } = vizState;

    amchartIdGlobal = amchartId
    client = clientName
    clientColors = chartColors[client]

    // get subquery for the chart
    let squeryItem = subquery.getSubqueryForItem(amchartId)

    let svizState = !!squeryItem && JSON.parse(squeryItem.vizState)

    let subqueryProps = {
        resultSet: useCubeQuery(svizState?.query, { subscribe: true, cubejsApi }).resultSet,
        pivotConfig: svizState?.pivotConfig
    }


    let renderProps = useCubeQuery(query, { subscribe: true, cubejsApi });


    let component = !!renderProps.resultSet && renderProps.resultSet.loadResponse.results[0].data.length <= 0 ?
        TypeToMemoChartComponent['noChart'] :
        TypeToMemoChartComponent[chartType];

    const getAttribute = (attributeName) => chart.getDashboardItemAttribute(amchartId, attributeName)

    const props = {
        ...renderProps,
        pivotConfig,
        orientation: getAttribute('orientation'),
        hasLegend: getAttribute('hasLegend'),
        isSentiment: getAttribute('isSentiment'),
        isDateAxis: getAttribute('isDateAxis'),
        amchartTitle: amchartTitle,
        renderedCharts: renderedCharts
    }


    if (!!squeryItem) {
        return component && renderChartWSubQuery(component)({ ...props, subqueryProps });
    }

    return component && renderChart(component)(props);

}

ChartRenderer.propTypes = {
    vizState: PropTypes.object,
    cubejsApi: PropTypes.object,
    amchartId: PropTypes.string
};
ChartRenderer.defaultProps = {
    vizState: {},
    cubejsApi: null,
    amchartId: null
};
export default ChartRenderer;
